/* eslint-disable curly */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import { ensureConfig } from '@edx/frontend-platform';
import { AppContext } from '@edx/frontend-platform/react';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Alert, Hyperlink } from '@edx/paragon';

// Actions
import {
  fetchProfile,
  saveProfile,
  saveProfilePhoto,
  deleteProfilePhoto,
  openForm,
  closeForm,
  updateDraft,
  fetchPersonalizationCatalog,
} from './data/actions';

// Components
import ProfileAvatar from './forms/ProfileAvatar';
import YearOfBirth from './forms/YearOfBirth';
import Country from './forms/Country';
import ProfSphere from './forms/ProfSphere';
import LearningGoals from './forms/LearningGoals';
import City from './forms/City';
import SocialLinks from './forms/SocialLinks';
import Certificates from './forms/Certificates';
import PageLoading from './PageLoading';
import { getAccountData } from './data/services';
import MainInformation from './MainInformation';
import UserDataItem from './UserDataItem';
import CustomProgressBar from './components/CustomProgressBar/CustomProgressBar';
import TagsSection from './components/TagsSection/TagsSection';
import VulnerabilityStatus from './forms/VulnerabilityStatus';

// Selectors
import { profilePageSelector } from './data/selectors';

// i18n
import messages from './ProfilePage.messages';
import withParams from '../utils/hoc';
import LinkedInCertificatesBlock from './components/LinkedInCertificatesBlock/LinkedInCertificatesBlock';

ensureConfig(['CREDENTIALS_BASE_URL', 'LMS_BASE_URL'], 'ProfilePage');

const ProfilePage = ({
  fetchProfile,
  saveProfile,
  saveProfilePhoto,
  deleteProfilePhoto,
  openForm,
  closeForm,
  updateDraft,
  fetchPersonalizationCatalog,
  params,
  profileImage,
  savePhotoState,
  name,
  visibilityName,
  yearOfBirth,
  visibilityYearOfBirth,
  country,
  visibilityCountry,
  city,
  visibilityCity,
  levelOfEducation,
  visibilityLevelOfEducation,
  socialLinks,
  draftSocialLinksByPlatform,
  visibilitySocialLinks,
  learningGoal,
  visibilityLearningGoal,
  languageProficiencies,
  visibilityLanguageProficiencies,
  courseCertificates,
  visibilityCourseCertificates,
  bio,
  visibilityBio,
  requiresParentalConsent,
  isLoadingProfile,
  dateJoined,
  photoUploadError,
  personalizationCatalog,
  isLoadingPersonalizationCatalog,
  intl,
}) => {
  const context = useContext(AppContext);
  const [viewMyRecordsUrl, setViewMyRecordsUrl] = useState(null);
  const [accountSettingsUrl, setAccountSettingsUrl] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [changeAccountData, setChangeAccountData] = useState(false);
  const [submitChangeData, setSubmitChangeData] = useState(false);
  const [submitChangeDataCustom, setSubmitChangeDataCustom] = useState(false);
  const [catalog, setCatalog] = useState([]);

  // Стейт для контролю видимості фіксованої кнопки
  const [isFixedButtonVisible, setIsFixedButtonVisible] = useState(true);

  useEffect(() => {
    const credentialsBaseUrl = context.config.CREDENTIALS_BASE_URL;
    setViewMyRecordsUrl(credentialsBaseUrl ? `${credentialsBaseUrl}/records` : null);
    setAccountSettingsUrl(`${context.config.LMS_BASE_URL}/account/settings`);

    const fetchAccount = async () => {
      const data = await getAccountData(params.username);
      setAccountData(data);
    };
    fetchAccount();

    fetchProfile(params.username);
    fetchPersonalizationCatalog();

    sendTrackingLogEvent('edx.profile.viewed', { username: params.username });
  }, [context.config, fetchProfile, fetchPersonalizationCatalog, params.username]);

  useEffect(() => {
    const handleScroll = () => {
      // Check if it's a mobile device
      const isMobile = window.innerWidth <= 767;

      if (!isMobile) {
        setIsFixedButtonVisible(false);
        return;
      }

      const footer = document.querySelector('footer');
      if (!footer) {
        setIsFixedButtonVisible(true);
        return;
      }

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const shouldHideButton = footerRect.top <= windowHeight;
      setIsFixedButtonVisible(!shouldHideButton);
    };

    const handleResize = () => {
      handleScroll();
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch account data after all saves are complete
  useEffect(() => {
    if (!submitChangeData && !submitChangeDataCustom && !changeAccountData) {
      // All saves are complete, fetch updated account data
      const fetchUpdatedAccount = async () => {
        try {
          const updatedData = await getAccountData(params.username);
          setAccountData(updatedData);
        } catch (error) {
          console.error('Error fetching updated account data:', error);
        }
      };

      // Add a small delay to ensure all saves have been processed on the server
      const timeoutId = setTimeout(fetchUpdatedAccount, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [submitChangeData, submitChangeDataCustom, changeAccountData, params.username]);

  useEffect(() => {
    if (!isLoadingPersonalizationCatalog && personalizationCatalog) {
      setCatalog(personalizationCatalog);
    }
  }, [personalizationCatalog, isLoadingPersonalizationCatalog]);

  const handleSaveProfilePhoto = formData => {
    saveProfilePhoto(context.authenticatedUser.username, formData);
  };

  const handleDeleteProfilePhoto = () => {
    deleteProfilePhoto(context.authenticatedUser.username);
  };

  const handleCourseInterestsUpdate = categories => {
    updateDraft('courseInterests', categories);
  };

  const handleDesiredSkillsUpdate = skills => {
    updateDraft('desiredSkills', skills);
  };

  const changeAccountDataProfile = async () => {
    // Save all profile data at once with a single action
    await saveProfile('profile', context.authenticatedUser.username);
    setChangeAccountData(false);
    // Don't trigger individual component saves
    // setSubmitChangeData(true);
    // setSubmitChangeDataCustom(true);
  };

  const closeAccountDataProfile = () => {
    setChangeAccountData(false);
  };

  const handleClose = formId => {
    closeForm(formId);
  };

  const handleOpen = formId => {
    openForm(formId);
  };

  const handleSubmit = async formId => {
    await saveProfile(formId, context.authenticatedUser.username);
  };

  const handleChange = (name, value) => {
    updateDraft(name, value);
  };

  const handleProfileChangeClick = () => {
    setChangeAccountData(true);

    // Ensure personalization catalog is available for ProfSpheres component
    if (!personalizationCatalog && !isLoadingPersonalizationCatalog) {
      fetchPersonalizationCatalog();
    }
  };

  const isAuthenticatedUserProfile = () => params.username === context.authenticatedUser.username;

  const renderViewMyRecordsButton = () => {
    if (!viewMyRecordsUrl || !isAuthenticatedUserProfile()) return null;
    return (
      <Hyperlink className="btn btn-primary mb-4" destination={viewMyRecordsUrl} target="_blank">
        {intl.formatMessage(messages['profile.viewMyRecords'])}
      </Hyperlink>
    );
  };

  const renderPhotoUploadErrorMessage = () => {
    if (!photoUploadError) return null;
    return (
      <div className="row">
        <div className="">
          <Alert variant="danger" dismissible={false} show>
            {photoUploadError.userMessage}
          </Alert>
        </div>
      </div>
    );
  };

  const isBlockVisible = blockInfo => isAuthenticatedUserProfile() || !!blockInfo;

  const renderContent = () => {
    if (isLoadingProfile) {
      return <PageLoading srMessage={intl.formatMessage(messages['profile.loading'])} />;
    }

    const commonFormProps = {
      openHandler: handleOpen,
      closeHandler: handleClose,
      submitHandler: handleSubmit,
      changeHandler: handleChange,
    };

    return (
      <div className="xl:tw-grid xl:tw-grid-cols-[376px,1fr]">
        <div className="tw-h-full tw-bg-body tw-pt-8 tw-px-6 md:tw-px-8 xl:tw-px-[48px] xl:tw-w-[376px] ">
          <div
            className={`tw-flex tw-gap-8 tw-flex-col md:tw-flex-row md:tw-justify-between xl:tw-flex-col xl:tw-justify-start ${changeAccountData ? 'xl:tw-gap-8' : 'xl:tw-gap-12'}`}
          >
            <div
              className={`tw-flex tw-gap-4 ${changeAccountData ? 'tw-w-full md:tw-w-[50%] md:tw-gap-4 xl:md:tw-gap-6' : 'md:tw-flex-row md:tw-gap-6'} xl:tw-flex-col xl:tw-w-full`}
            >
              <ProfileAvatar
                className="tw-mb-md-3"
                src={profileImage.src}
                isDefault={profileImage.isDefault}
                onSave={handleSaveProfilePhoto}
                onDelete={handleDeleteProfilePhoto}
                savePhotoState={savePhotoState}
                // isEditable={isAuthenticatedUserProfile() && !requiresParentalConsent}
                isEditable={false}
                name={name}
              />
              <div
                className={`${changeAccountData ? 'tw-w-full md:tw-w-auto xl:tw-text-start xl:tw-mx-4' : 'xl:tw-text-center'}`}
              >
                {accountData && accountData.personalization && (
                  <MainInformation
                    name={name}
                    date={dateJoined}
                    isEditMode={changeAccountData}
                    changeHandler={handleChange}
                    initialRole={accountData.personalization.professionalRole}
                  />
                )}
              </div>
            </div>
            <div className="md:tw-min-w-[350px] xl:tw-min-w-0">
              <SocialLinks
                socialLinks={socialLinks}
                changeData={changeAccountData}
                saveData={submitChangeData}
                onSaveComplete={() => setSubmitChangeData(false)}
                draftSocialLinksByPlatform={draftSocialLinksByPlatform}
                visibilitySocialLinks={visibilitySocialLinks}
                formId="socialLinks"
                {...commonFormProps}
              />
              {/* Десктопна версія кнопок - приховати на мобільних */}
              <div className="tw-hidden md:tw-block md:tw-mt-8 xl:tw-mt-14">
                {!changeAccountData && (
                  <div className="tw-flex tw-flex-col xl:tw-items-center">
                    <button
                      type="button"
                      onClick={handleProfileChangeClick}
                      className="button-primary tw-flex tw-justify-center tw-items-center xl:tw-w-full tw-h-[45px] tw-border-none tw-font-medium"
                    >
                      {intl.formatMessage(messages.editButton)}
                    </button>
                  </div>
                )}
                {changeAccountData && (
                  <div className="tw-flex tw-gap-2 md:tw-flex-row-reverse xl:tw-flex-col xl:tw-px-4">
                    <button
                      type="button"
                      onClick={() => changeAccountDataProfile()}
                      className="button-primary tw-flex tw-justify-center tw-items-center tw-w-[178px] tw-h-[45px] tw-border-none tw-font-medium xl:tw-w-full"
                    >
                      {intl.formatMessage(messages.saveButton)}
                    </button>
                    <button
                      type="button"
                      onClick={() => closeAccountDataProfile()}
                      className="button-cancel tw-flex tw-justify-center tw-items-center tw-w-[202px] tw-h-[45px] tw-font-medium md:tw-px-3 xl:tw-w-full"
                    >
                      {intl.formatMessage(messages.cancelButton)}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-none d-md-block">{renderViewMyRecordsButton()}</div>
          {renderPhotoUploadErrorMessage()}
        </div>

        <div className="tw-min-w-0 tw-bg-white tw-rounded-t-[32px] tw-min-h-[calc(100dvh-130px)] tw-mb-[-80px] tw-rounded-b-[32px] xl:tw-mb-0 xl:tw-min-h-0 xl:tw-h-[calc(100%+50px)] xl:tw-rounded-t-none xl:tw-rounded-br-[32px] xl:tw-rounded-bl-none">
          <div className="tw-container tw-py-10 tw-pl-6 tw-col-span-2 tw-mt-[32px] md:tw-mx-0 md:tw-max-w-none md:tw-py-12 md:tw-pl-12 xl:tw-mt-0 xl:tw-pl-[80px] xl:tw-pt-[56px]">
            <div className="tw-mb-10 tw-pr-6 md:tw-mb-[80px] md:tw-pr-12 xl:tw-mb-14 xl:tw-pr-[80px]">
              <div className="tw-flex tw-justify-between tw-mb-8 md:tw-gap-16">
                <div>
                  <h6 className="tw-text-2xl tw-font-medium tw-leading-[120%] md:tw-text-[28px]">
                    {changeAccountData
                      ? intl.formatMessage(messages.profileTitleEditing)
                      : intl.formatMessage(messages.profileTitle)}
                  </h6>
                  {!changeAccountData && (
                    <p className="tw-mt-4">{intl.formatMessage(messages.profileSubtitle)}</p>
                  )}
                </div>
              </div>
              {accountData && accountData.personalization && (
                <CustomProgressBar
                  value={accountData.personalization.profileCompletionPercentage}
                />
              )}
            </div>
            <div className="tw-grid tw-grid-cols-1 tw-mb-10 tw-pr-6 md:tw-grid-cols-[320px,1fr] md:tw-gap-x-[40px] md:tw-mb-[80px] md:tw-pr-12 lg:tw-gap-x-[80px] xl:tw-mb-14 xl:tw-pr-[80px] xl:tw-gap-x-[96px]">
              <div>
                <div className="d-md-none">{renderViewMyRecordsButton()}</div>
                {accountData && (
                  <div>
                    <UserDataItem
                      data={name}
                      changeData={changeAccountData}
                      label={intl.formatMessage(messages['profile.name.full.name'])}
                      onSaveComplete={() => setSubmitChangeDataCustom(false)}
                      saveData={submitChangeDataCustom}
                      field="name"
                      username=""
                    />
                    <UserDataItem
                      data={accountData.gender}
                      username={params.username}
                      field="gender"
                      onSaveComplete={() => setSubmitChangeDataCustom(false)}
                      changeData={changeAccountData}
                      saveData={false}
                      label={intl.formatMessage(messages['profile.user.gender'])}
                      changeHandler={handleChange}
                    />
                  </div>
                )}
                {isBlockVisible(yearOfBirth) && (
                  <YearOfBirth
                    yearOfBirth={yearOfBirth}
                    changeData={changeAccountData}
                    saveData={submitChangeData}
                    onSaveComplete={() => setSubmitChangeData(false)}
                    visibilityYearOfBirth={visibilityYearOfBirth}
                    formId="yearOfBirth"
                    {...commonFormProps}
                  />
                )}
                {isBlockVisible(country) && (
                  <Country
                    country={country || ''}
                    changeData={changeAccountData}
                    saveData={submitChangeData}
                    onSaveComplete={() => setSubmitChangeData(false)}
                    visibilityCountry={visibilityCountry}
                    formId="country"
                    {...commonFormProps}
                  />
                )}
                {isBlockVisible(city) && (
                  <City
                    city={city}
                    changeData={changeAccountData}
                    saveData={submitChangeData}
                    onSaveComplete={() => setSubmitChangeData(false)}
                    visibilityCity={visibilityCity}
                    formId="city"
                    {...commonFormProps}
                  />
                )}
                {accountData && accountData.personalization && (
                  <ProfSphere
                    profSphere={accountData.personalization.professionalSphere}
                    changeData={changeAccountData}
                    saveData={submitChangeData}
                    onSaveComplete={() => setSubmitChangeData(false)}
                    spheres={catalog.professionalSpheres || []}
                    formId="professionalSphere"
                    {...commonFormProps}
                  />
                )}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-10">
                {accountData && accountData.personalization && (
                  <>
                    <LearningGoals
                      learningGoal={accountData.personalization.learningGoals?.[0] || null}
                      changeData={changeAccountData}
                      saveData={submitChangeData}
                      onSaveComplete={() => setSubmitChangeData(false)}
                      goals={catalog.learningGoals || []}
                      formId="learningGoals"
                      {...commonFormProps}
                    />
                    <TagsSection
                      title={intl.formatMessage(messages.userInterestsTitle)}
                      placeholder={intl.formatMessage(messages.userInterestsPlaceholder)}
                      userCategories={accountData.personalization.courseInterests}
                      categories={catalog.courseCategories}
                      changeData={changeAccountData}
                      editButton={intl.formatMessage(messages.userInterestsEditButton)}
                      onCategoriesUpdate={handleCourseInterestsUpdate}
                    />
                    <TagsSection
                      title={intl.formatMessage(messages.userSkillsTitle)}
                      placeholder={intl.formatMessage(messages.userSkillsPlaceholder)}
                      userCategories={accountData.personalization.desiredSkills}
                      categories={catalog.skillCategories}
                      changeData={changeAccountData}
                      editButton={intl.formatMessage(messages.userSkillsEditButton)}
                      onCategoriesUpdate={handleDesiredSkillsUpdate}
                    />
                    <VulnerabilityStatus
                      vulnerabilityStatus={accountData.personalization.vulnerabilityStatus || ''}
                      changeData={changeAccountData}
                      saveData={submitChangeData}
                      onSaveComplete={() => setSubmitChangeData(false)}
                      formId="vulnerabilityStatus"
                      {...commonFormProps}
                    />
                  </>
                )}
              </div>
            </div>
            {!changeAccountData && <Certificates formId="certificates" {...commonFormProps} />}
            {!changeAccountData && !!courseCertificates?.length && <LinkedInCertificatesBlock />}
          </div>
        </div>

        {/* Фіксована кнопка для мобільних пристроїв */}
        <div
          className="tw-block tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[1050] tw-p-4 md:tw-hidden"
          style={{
            opacity: isFixedButtonVisible ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          {!changeAccountData && (
            <button
              type="button"
              onClick={handleProfileChangeClick}
              className="button-primary button-lg tw-w-full tw-font-medium"
            >
              {intl.formatMessage(messages.editButton)}
            </button>
          )}
          {changeAccountData && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <button
                type="button"
                onClick={() => changeAccountDataProfile()}
                className="button-primary button-lg tw-w-full tw-font-medium"
              >
                {intl.formatMessage(messages.saveButton)}
              </button>
              <button
                type="button"
                onClick={() => closeAccountDataProfile()}
                className="button-cancel button-lg tw-w-full tw-font-medium"
              >
                {intl.formatMessage(messages.cancelButton)}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return <div className="profile-page">{renderContent()}</div>;
};

ProfilePage.propTypes = {
  requiresParentalConsent: PropTypes.bool,
  dateJoined: PropTypes.string,
  bio: PropTypes.string,
  yearOfBirth: PropTypes.number,
  visibilityYearOfBirth: PropTypes.string,
  visibilityBio: PropTypes.string.isRequired,
  courseCertificates: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
    }),
  ),
  visibilityCourseCertificates: PropTypes.string.isRequired,
  country: PropTypes.string,
  visibilityCountry: PropTypes.string.isRequired,
  city: PropTypes.string,
  visibilityCity: PropTypes.string,
  levelOfEducation: PropTypes.string,
  visibilityLevelOfEducation: PropTypes.string.isRequired,
  languageProficiencies: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  ),
  visibilityLanguageProficiencies: PropTypes.string.isRequired,
  name: PropTypes.string,
  visibilityName: PropTypes.string.isRequired,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      platform: PropTypes.string,
      socialLink: PropTypes.string,
    }),
  ),
  draftSocialLinksByPlatform: PropTypes.objectOf(
    PropTypes.shape({
      platform: PropTypes.string,
      socialLink: PropTypes.string,
    }),
  ),
  visibilitySocialLinks: PropTypes.string.isRequired,
  learningGoal: PropTypes.string,
  visibilityLearningGoal: PropTypes.string,
  profileImage: PropTypes.shape({
    src: PropTypes.string,
    isDefault: PropTypes.bool,
  }),
  saveState: PropTypes.oneOf([null, 'pending', 'complete', 'error']),
  savePhotoState: PropTypes.oneOf([null, 'pending', 'complete', 'error']),
  isLoadingProfile: PropTypes.bool.isRequired,
  photoUploadError: PropTypes.objectOf(PropTypes.string),
  fetchProfile: PropTypes.func.isRequired,
  saveProfile: PropTypes.func.isRequired,
  saveProfilePhoto: PropTypes.func.isRequired,
  deleteProfilePhoto: PropTypes.func.isRequired,
  openForm: PropTypes.func.isRequired,
  closeForm: PropTypes.func.isRequired,
  updateDraft: PropTypes.func.isRequired,
  params: PropTypes.shape({
    username: PropTypes.string.isRequired,
  }).isRequired,
  intl: intlShape.isRequired,
};

ProfilePage.defaultProps = {
  saveState: null,
  savePhotoState: null,
  photoUploadError: {},
  profileImage: {},
  name: null,
  yearOfBirth: null,
  visibilityYearOfBirth: null,
  levelOfEducation: null,
  country: '',
  city: null,
  visibilityCity: 'private',
  socialLinks: [],
  draftSocialLinksByPlatform: {},
  bio: null,
  learningGoal: null,
  languageProficiencies: [],
  courseCertificates: null,
  requiresParentalConsent: null,
  dateJoined: null,
  visibilityLearningGoal: null,
};

export default connect(profilePageSelector, {
  fetchProfile,
  saveProfilePhoto,
  deleteProfilePhoto,
  saveProfile,
  openForm,
  closeForm,
  updateDraft,
  fetchPersonalizationCatalog,
})(injectIntl(withParams(ProfilePage)));
